import React, {useState, useEffect, useRef} from 'react';
import DatePicker from 'tailwind-datepicker-react';
import {IOptions} from 'tailwind-datepicker-react/types/Options';
import {CalendarMonth} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {formatDateBackendString} from "../../utils/daysOptions";
import {CustomTypography} from "./CustomTypography";

export interface CustomDatePicketProps {
    label: string;
    className?: string;
    minDate?: Date
    value?: Date;
    setValue?: any;
    disabled?: boolean;
    mandatory?: boolean;
    error?: string;
}

const CustomDatePicket = ({
                              label,
                              className,
                              disabled,
                              mandatory,
                              value,
                              setValue,
                              minDate,
                              error,
                          }: CustomDatePicketProps) => {
    const {t} = useTranslation('global');
    const [show, setShow] = useState<boolean>(false);
    const datePickerRef = useRef(null);
    const [dateAux, setDateAux] = useState<Date | null>()

    const handleChange = (selectedDate: any) => {
        setDateAux(selectedDate);
    };

    useEffect(() => {
        if (!show && dateAux) {
            setValue(dateAux)
        }
    }, [show, dateAux]);

    const handleClose = () => {
        setShow(false);
    };

    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            const datePickerElement: any = datePickerRef.current;

            // Si el DatePicker está abierto y el clic no ocurre dentro del DatePicker, cierra el DatePicker
            if (show && datePickerElement && !datePickerElement.contains(e.target as HTMLElement)) {
                handleClose();
            }
        };

        if (show) {
            document.addEventListener('click', handleDocumentClick);
        } else {
            document.removeEventListener('click', handleDocumentClick);
        }

        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [show]);

    const [options, setOptions] = useState<IOptions>({
        autoHide: true,
        todayBtn: false,
        clearBtn: false,
        language: 'es',
        datepickerClassNames: 'datepicker-container top-50 mt-1 tooltip border-[1px] rounded-[10px] p-0',
        defaultDate: getValueDatePicket(value, minDate),
        minDate: minDate,
    });

    const containerClassName = `fixed top-0 left-0 w-full h-full backdrop-filter backdrop-blur-[5px] bg-opacity-70 bg-black z-[1200] flex items-center justify-center`;

    const [animation, setAnimation] = useState(false)

    const internalClassName = `w-[298px] h-[382px] z-[99991] ${
        animation ? 'fade-out' : 'fade-in'
    }`;

    const prevShow = (prev: boolean) => {
        if (!prev) {
            setAnimation(true)
            const timeoutId = setTimeout(() => {
                setShow(prev)
                setAnimation(false);
            }, 300);
            return () => clearTimeout(timeoutId);
        } else {
            setShow(prev)
        }
    }

    const dataPicketOptions = () => {
        return (
            <>
                {show && (
                    <div className={containerClassName}>
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            className={internalClassName}
                        >
                            <DatePicker options={options} onChange={handleChange} show={show}
                                        setShow={(prev: boolean) => {
                                            prevShow(prev)
                                        }}/>
                        </div>
                    </div>
                )}
            </>
        );
    }

    useEffect(() => {
        if (minDate) {
            setOptions((prevOptions) => ({
                ...prevOptions,
                defaultDate: getValueDatePicket(value, minDate),
                minDate: minDate,
            }));
        }
    }, [minDate]);

    useEffect(() => {
        if (value) {
            setOptions((prevOptions) => ({
                ...prevOptions,
                defaultDate: getValueDatePicket(value, minDate),
            }));
        }
    }, [value]);

    return (
        <div className={`${className} relative min-h-[52px] w-full ${disabled ? 'pointer-events-none' : ''}`} ref={datePickerRef}>
            {(value || mandatory) && (
                <label
                    className={getStyleLabelDatePicket(error, disabled, mandatory)}>
                    {label}
                    {mandatory && <span className={'text-danger ml-[2px]'}>*</span>}
                </label>
            )}
            <div
                onClick={() => {
                    setShow(!show);
                }}
                className={getStyleContentDatePicket(error, disabled, show)}
            >
                <div
                    className={getStyleBodyDatePicket('text', error, value, disabled, show)}>
                    {getValue(value, label, mandatory)}
                </div>
                <div className={'pr-1 flex self-center w-[20%] justify-end'}>
                    <CalendarMonth className={getStyleBodyDatePicket('icon', error, value, disabled, show)}/>
                </div>
            </div>
            {error && (
                <CustomTypography variant="small" style={{color: 'red'}} className="font-normal ml-1 text-[10px]" text={t('obligatory-field')}/>
            )}
            {dataPicketOptions()}
        </div>
    );
};

function getValue(value: Date | undefined, label: string, mandatory: boolean | undefined) {
    if (value) {
        return formatDateBackendString(value)
    } else if (mandatory) {
        return '--/--/--'
    } else {
        return label
    }
}

function getValueDatePicket(value: Date | undefined, minDate: Date | undefined) {
    return value ? value : minDate
}

function getStyleLabelDatePicket(error: string | undefined, disabled: boolean | undefined, mandatory: boolean | undefined) {
    return `${disabled ? 'text-colorDisabled' : 'text-labelColor'} normal-case text-[10px] absolute bg-white ${mandatory ? 'mt-[-9px]' : 'mt-[-7px]'} ml-[18px] labelCustom px-1 rounded-[5px]`
}

function getStyleContentDatePicket(error: string | undefined, disabled: boolean | undefined, show: boolean) {
    return `styleInput text-input !border 
                ${error ? '!border-danger text-danger placeholder:text-danger' : disabled ? '!border-colorDisabled' : show ? '!border-primary' : '!border-inputBorder'} 
                flex min-h-[40px] max-h-[40px] rounded-[4px] shadow-lg shadow-gray-900/5 ring-1 ring-transparent 
                placeholder:text-gray-500 focus:!border-primary 
                ${disabled ? 'pointer-events-none' : 'cursor-pointer'}`
}

function getStyleBodyDatePicket(type: string,error: string | undefined, value: Date | undefined, disabled: boolean | undefined, show: boolean | undefined) {
    if (type === 'icon') {
        return `w-[20px] mr-2 ${disabled ? 'text-colorDisabled' : show ? 'text-primary' : 'text-inputBorder'}`
    } else {
        return `font-roboto text-[14px] self-center w-[80%] ml-3 ${error ? 'text-danger' : value ? 'text-input' : 'text-inputBorder text-[13px]'}`
    }
}

export default CustomDatePicket;
